import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, CheckCircle, QrCode, Download, Share2, Clock, XCircle, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';

export function GatePassSection({
  activeGatePasses,
  pendingApprovalPasses,
  setShowPreApprovalModal,
  user,
  setSelectedGatePass,
  setShowQRModal,
  onApprove,
  onReject
}) {
  return (
    <>
      <Card className="shadow-lg border-0 bg-gradient-to-r from-gk-teal to-gk-aqua text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Pre-Approve Your Visitors</h3>
              <p className="text-emerald-50 mb-4">Generate a gate pass with QR code for hassle-free entry</p>
              <Button
                data-testid="create-preapproval-button"
                onClick={() => setShowPreApprovalModal('picker')}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Gate Pass
              </Button>
            </div>
            <div className="text-right">
              <p className="text-5xl font-bold mb-2">{activeGatePasses.length + (pendingApprovalPasses?.length || 0)}</p>
              <p className="text-emerald-50">Gate Passes</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending approval passes */}
      {pendingApprovalPasses && pendingApprovalPasses.length > 0 && (
        <Card className="shadow-lg border-0 border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <Clock className="w-5 h-5" />
              Pending Your Approval ({pendingApprovalPasses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovalPasses.map((pass) => (
                <div
                  key={pass.id}
                  data-testid={`pending-pass-${pass.id}`}
                  className="p-4 bg-amber-50 rounded-xl border border-amber-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-900">{pass.visitor_name}</p>
                      <p className="text-xs text-slate-500">{pass.visitor_phone || 'No phone'}</p>
                      <p className="text-xs text-slate-500">Villa {pass.villa_number}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium">Awaiting Approval</span>
                      <p className="text-[10px] text-slate-400 mt-1">Code: {pass.access_code}</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">Guard registered this visitor. Approve to grant entry or reject to deny.</p>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-pass-${pass.id}`}
                      onClick={() => onApprove(pass.id)}
                      size="sm"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve Entry
                    </Button>
                    <Button
                      data-testid={`reject-pass-${pass.id}`}
                      onClick={() => onReject(pass.id)}
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeGatePasses.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 sm:gap-3">
              <CheckCircle className="w-5 h-5" />
              Active Gate Passes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeGatePasses.map((pass) => (
                <div
                  key={pass.id}
                  data-testid={`gate-pass-${pass.id}`}
                  className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg">{pass.visitor_name}</p>
                      <p className="text-sm text-slate-600">{pass.visitor_phone}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Valid until: {new Date(pass.valid_until).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-3xl font-mono font-bold text-emerald-600">{pass.access_code}</p>
                      <p className="text-xs text-slate-500">Access Code</p>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedGatePass(pass);
                          setShowQRModal(true);
                        }}
                        className="bg-gk-teal hover:bg-gk-teal-hover"
                      >
                        <QrCode className="w-4 h-4 mr-1" />
                        Show QR
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export function GatePassModals({
  showPreApprovalModal,
  setShowPreApprovalModal,
  preApprovalForm,
  setPreApprovalForm,
  handleCreatePreApproval,
  showQRModal,
  setShowQRModal,
  selectedGatePass,
  generateQRData,
  downloadQRCode,
  shareGatePass,
  user
}) {
  return (
    <>
      <Dialog open={showPreApprovalModal} onOpenChange={setShowPreApprovalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Gate Pass</DialogTitle>
            <DialogDescription>Generate a pre-approval gate pass for your visitor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Visitor Name</label>
              <input
                data-testid="visitor-name-input"
                type="text"
                value={preApprovalForm.visitor_name}
                onChange={(e) => setPreApprovalForm({...preApprovalForm, visitor_name: e.target.value})}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter visitor name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Visitor Phone</label>
              <input
                data-testid="visitor-phone-input"
                type="tel"
                value={preApprovalForm.visitor_phone}
                onChange={(e) => setPreApprovalForm({...preApprovalForm, visitor_phone: e.target.value})}
                className="w-full p-2 border rounded-lg"
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Valid Hours</label>
              <select
                data-testid="valid-hours-select"
                value={preApprovalForm.valid_hours}
                onChange={(e) => setPreApprovalForm({...preApprovalForm, valid_hours: e.target.value})}
                className="w-full p-2 border rounded-lg"
              >
                <option value={1}>1 Hour</option>
                <option value={2}>2 Hours</option>
                <option value={4}>4 Hours</option>
                <option value={8}>8 Hours</option>
                <option value={24}>24 Hours</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="generate-pass-button"
                onClick={handleCreatePreApproval}
                className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
              >
                Generate Pass
              </Button>
              <Button
                onClick={() => setShowPreApprovalModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Gate Pass
            </DialogTitle>
            <DialogDescription>Share this with your visitor for entry</DialogDescription>
          </DialogHeader>
          {selectedGatePass && (
            <div className="px-6 pb-6 space-y-4 pt-2">
              {/* Capturable card — this entire div gets rendered to image */}
              <div
                id="gate-pass-card"
                data-testid="gate-pass-card"
                style={{ background: '#ffffff', padding: '24px', borderRadius: '12px' }}
              >
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>GateKeeper Pro</p>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0 0' }}>Visitor Gate Pass</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '2px solid #bbf7d0' }}>
                  <QRCodeSVG
                    id="gate-pass-qr"
                    value={generateQRData(selectedGatePass)}
                    size={180}
                    level="H"
                    includeMargin={true}
                    bgColor="#f0fdf4"
                    fgColor="#000000"
                  />
                </div>
                <div style={{ textAlign: 'center', margin: '12px 0' }}>
                  <p style={{ fontSize: '36px', fontFamily: 'monospace', fontWeight: 'bold', color: '#059669', margin: 0, letterSpacing: '6px' }}>
                    {selectedGatePass.access_code}
                  </p>
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0 0' }}>4-Digit Access Code</p>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', fontSize: '13px', lineHeight: '1.7' }}>
                  <p style={{ margin: 0 }}><span style={{ color: '#64748b' }}>Visitor:</span> <strong>{selectedGatePass.visitor_name}</strong></p>
                  <p style={{ margin: 0 }}><span style={{ color: '#64748b' }}>Phone:</span> <strong>{selectedGatePass.visitor_phone}</strong></p>
                  <p style={{ margin: 0 }}><span style={{ color: '#64748b' }}>Villa:</span> <strong>{user?.villa_number}</strong></p>
                  <p style={{ margin: 0 }}><span style={{ color: '#64748b' }}>Host:</span> <strong>{user?.name}</strong></p>
                  <p style={{ margin: 0 }}><span style={{ color: '#64748b' }}>Valid Until:</span> <strong style={{ color: '#059669' }}>{new Date(selectedGatePass.valid_until).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</strong></p>
                </div>
                <p style={{ textAlign: 'center', fontSize: '10px', color: '#94a3b8', marginTop: '8px' }}>
                  Show this QR code or the 4-digit code at the gate | gatekeeper.homes
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="download-gate-pass"
                  onClick={downloadQRCode}
                  className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  data-testid="share-gate-pass"
                  onClick={shareGatePass}
                  variant="outline"
                  className="flex-1"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
